<template>
  <div class="modal-backdrop">
    <div class="modal-content">
      <h3>
        Review Gun <i>{{ gunName }}</i>
      </h3>
      <h4>
        {{
          validationStatus == "change venturi diameter"
            ? "Venturi nozzle needs to be replaced"
            : "Line needs maintenance and rebagging"
        }}
        <i v-if="gunRecipes.length > 0">({{ ((1 - realThroughput / targetThroughput) * 100).toFixed(0) }}% off)</i>
      </h4>

      <SteelCaseGunsEqualizationChart
        :datasets="[gunDataset]"
        :recipes="gunRecipes"
        :show-controls="false"
      />
      <div
        class="actions mt-4 mb-0"
        style="justify-content: center"
      >
        <button
          class="btn"
          style="padding: 12px 20px; font-size: 15px"
          :class="
            validationStatus == 'pending_change_venturi' || validationStatus == 'rejected'
              ? 'reject-selected'
              : 'reject-faded'
          "
          @click="discardMeasurement"
        >
          Ignore Warning
        </button>
        <button
          class="btn"
          style="padding: 12px 20px; font-size: 15px"
          :class="
            validationStatus == 'changing_venturi' || validationStatus == 'rebagging' ? 'alert-selected' : 'alert-faded'
          "
          @click="changeVenturi"
        >
          {{ changeVenturiStatus ? "Replace Venturi nozzle" : "Review Line Conditions" }}
        </button>
      </div>
      <hr class="horizontal-dark py-0" />
      <div
        v-if="totalNumberOfGuns > 1"
        class="actions mt-2"
        style="justify-content: center"
      >
        <button
          :disabled="gunReviewIndex <= 0"
          class="btn accept"
          :class="{ 'btn-disabled': gunReviewIndex <= 0 }"
          @click="previousGun"
        >
          Previous Gun
        </button>
        <button
          :disabled="gunReviewIndex >= totalNumberOfGuns - 1"
          class="btn accept"
          :class="{ 'btn-disabled': gunReviewIndex > totalNumberOfGuns - 1 }"
          @click="nextGun"
        >
          Next Gun
        </button>
      </div>
      <p
        v-if="totalNumberOfGuns > 1"
        class="pagination-indicator"
      >
        Gun {{ gunReviewIndex + 1 }} of {{ totalNumberOfGuns }}
      </p>
      <div
        class="actions"
        style="justify-content: center"
      >
        <button
          class="btn w-60"
          :class="pendingGunsToEvaluate.length > 0 ? 'btn-gray' : 'btn-success'"
          @click="finishReview"
        >
          Finish Review
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SteelCaseGunsEqualizationChart from "./SteelCaseGunsEqualizationChart.vue";

export default {
  name: "DiameterMeasurementGunReviewModal",
  components: {
    SteelCaseGunsEqualizationChart,
  },
  props: {
    gunName: {
      type: String,
      required: true,
    },
    gunReviewIndex: {
      type: Number,
      required: true,
    },
    validationStatus: {
      type: String,
      required: true,
    },
    gunDataset: {
      type: Object,
      required: true,
    },
    gunRecipes: {
      type: Array,
      default: () => [],
    },
    totalNumberOfGuns: {
      type: Number,
      default: 0,
    },
    pendingGunsToEvaluate: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "nextgunreview",
    "previousgunreview",
    "donereviewingfailedguns",
    "donereviewingchangeventuriguns",
    "updategunvalidationstatus",
  ],
  computed: {
    targetThroughput() {
      return Math.max(...this.gunRecipes.map(recipe => recipe.target_throughput));
    },
    realThroughput() {
      return this.gunRecipes[0].real_throughput;
    },
    changeVenturiStatus() {
      return (
        this.validationStatus === "change venturi diameter" ||
        this.validationStatus === "changing_venturi" ||
        this.validationStatus === "pending_change_venturi"
      );
    },
    rebaggingStatus() {
      return (
        this.validationStatus === "rebagging" ||
        this.validationStatus === "failure" ||
        this.validationStatus === "rejected"
      );
    },
  },
  methods: {
    changeVenturi() {
      if (this.changeVenturiStatus) {
        this.$emit("updategunvalidationstatus", "changing_venturi");
      } else if (this.rebaggingStatus) {
        this.$emit("updategunvalidationstatus", "rebagging");
      }
    },
    discardMeasurement() {
      if (this.changeVenturiStatus) {
        this.$emit("updategunvalidationstatus", "pending_change_venturi");
      } else if (this.rebaggingStatus) {
        this.$emit("updategunvalidationstatus", "rejected");
      }
    },
    finishReview() {
      if (this.pendingGunsToEvaluate.length > 0) {
        let gun_names_to_evaluate = this.pendingGunsToEvaluate.map(gun => gun.gun_name);
        this.$swal({
          title: "Warning",
          text: `The following guns are pending review: ${gun_names_to_evaluate.join(", ")}`,
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }

      if (this.rebaggingStatus) {
        this.$emit("donereviewingfailedguns");
      } else if (this.changeVenturiStatus) {
        this.$emit("donereviewingchangeventuriguns");
      }
    },
    nextGun() {
      this.$emit("nextgunreview");
    },
    previousGun() {
      this.$emit("previousgunreview");
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
}
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 800px;
  max-width: 90%;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}
.btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.accept {
  background: #4caf50;
  color: white;
}
.alert-selected {
  background: #ff9800;
  color: white;
}
.alert-faded {
  background: #ff9800;
  color: white;
  opacity: 0.5;
}
.pagination-indicator {
  text-align: center;
  font-size: 14px;
  color: #666;
}
.reject-selected {
  background: #f44336;
  color: white;
}
.reject-faded {
  background: #f44336;
  color: white;
  opacity: 0.5;
}
</style>
